import React, { useEffect, useRef, useCallback, useState } from 'react';
import { getNotificationMeta } from '../../utils/notificationMeta';

export interface ToastItem {
  id: string;
  typeId: number;
  title: string;
  message: string;
  contextData?: Record<string, string> | null;
}

interface NotificationToastContainerProps {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
  onToastClick?: (toast: ToastItem) => void;
}

function SingleToast({
  toast,
  onDismiss,
  onToastClick,
}: {
  toast: ToastItem;
  onDismiss: (id: string) => void;
  onToastClick?: (toast: ToastItem) => void;
}) {
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const [visible, setVisible] = useState(false);
  const meta = getNotificationMeta(toast.typeId);
  const clickable = Boolean(onToastClick);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    timerRef.current = setTimeout(() => onDismiss(toast.id), 5000);
    return () => clearTimeout(timerRef.current);
  }, [toast.id, onDismiss]);

  const handleClose = useCallback(() => {
    clearTimeout(timerRef.current);
    setVisible(false);
    setTimeout(() => onDismiss(toast.id), 300);
  }, [toast.id, onDismiss]);

  const handleClick = useCallback(() => {
    if (!onToastClick) return;
    clearTimeout(timerRef.current);
    onToastClick(toast);
    handleClose();
  }, [handleClose, onToastClick, toast]);

  return (
    <div
      className={`toast show border-0 shadow-lg mb-2 ${visible ? 'opacity-100' : 'opacity-0'} ${clickable ? 'cursor-pointer' : ''}`}
      role="alert"
      style={{ transition: 'opacity 0.3s ease', minWidth: 320 }}
      onClick={clickable ? handleClick : undefined}
    >
      <div className={`toast-header ${meta.bgColor} text-white`}>
        <i className={`fas fa-${meta.icon} me-2`} />
        <strong className="me-auto">{toast.title}</strong>
        <button
          type="button"
          className="btn-close btn-close-white"
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
          aria-label="Kapat"
        />
      </div>
      <div className="toast-body">{toast.message}</div>
    </div>
  );
}

const NotificationToastContainer: React.FC<NotificationToastContainerProps> = ({
  toasts,
  onDismiss,
  onToastClick,
}) => {
  if (toasts.length === 0) return null;

  return (
    <div
      className="position-fixed top-0 end-0 p-3"
      style={{ zIndex: 1090 }}
    >
      {toasts.map((t) => (
        <SingleToast key={t.id} toast={t} onDismiss={onDismiss} onToastClick={onToastClick} />
      ))}
    </div>
  );
};

export default NotificationToastContainer;
